import { useEffect, useRef, useState } from 'react';
import css from './index.module.scss';
import { useFormContext, useWatch } from 'react-hook-form';
import { formatCMValue, getCMTone, getCommaSeperatedCurrencyAmount } from '@helpers/utils';
import { Chip, Typography } from '@components/base';
import SelectLabel from '@components/common/select-label';
import {
  ICurrency,
  IFilterState,
  INonConsolidateQuote,
  IWeightChildOption
} from '@helpers/types/quotes';
import { ENQUIRY_TYPE } from '@helpers/constants';

interface ICostCalculatorProps {
  filters: IFilterState;
  weightOptions: IWeightChildOption[];
  currency: ICurrency;
  primaryUnitWatch: IWeightChildOption | null;
  quote: INonConsolidateQuote[];
  enquiryType?: string;
}

const CostCalculator = (props: ICostCalculatorProps) => {
  const {
    filters,
    weightOptions,
    currency,
    primaryUnitWatch,
    quote,
    enquiryType = ENQUIRY_TYPE.INTERNATIONAL
  } = props;
  const { consolidation, mtt } = filters;

  const wrapperClassName = `${css.wrapper} ${
    enquiryType != ENQUIRY_TYPE.INTERNATIONAL ? css.internationalWrapper : ''
  }`;

  return (
    <main className={css.mainWrapper}>
      <div className={wrapperClassName}>
        <Typography variant="h3" className={css.headerText}>
          Cost Calculator
        </Typography>
        {!consolidation
          ? quote?.map((item: any, index: number) => {
              return (
                <CostCalculatorCard
                  key={index}
                  item={item}
                  index={index}
                  currency={currency}
                  primaryUnitWatch={primaryUnitWatch}
                  weightOptions={weightOptions}
                  consolidation={consolidation}
                  mtt={mtt}
                  finalQuoteSellingPriceWeightUnit={`quote.${index}.finalQuote_selling_price.weightUnit`}
                  enquiryType={enquiryType}
                />
              );
            })
          : quote?.map((group: any, index: number) => {
              return (
                <div key={index} className={css.quoteWrapper}>
                  {group.product?.map((product: any, productIndex: number) => {
                    return (
                      <CostCalculatorCard
                        key={productIndex}
                        item={product}
                        quoteIndex={index}
                        index={productIndex}
                        currency={currency}
                        primaryUnitWatch={primaryUnitWatch}
                        weightOptions={weightOptions}
                        consolidation={consolidation}
                        mtt={mtt}
                        finalQuoteSellingPriceWeightUnit={`quote.${index}.product.${productIndex}.finalQuote_selling_price.weightUnit`}
                        enquiryType={enquiryType}
                      />
                    );
                  })}
                </div>
              );
            })}
      </div>
    </main>
  );
};

export default CostCalculator;

export interface CardItemProps {
  item: any;
  index: number;
  currency: ICurrency;
  primaryUnitWatch: IWeightChildOption | null;
  weightOptions: IWeightChildOption[];
  quoteIndex?: number;
  consolidation: boolean;
  mtt: boolean;
  finalQuoteSellingPriceWeightUnit?: any;
  enquiryType: string;
}

const CostCalculatorCard = (props: CardItemProps) => {
  const {
    item,
    index,
    currency,
    primaryUnitWatch,
    weightOptions,
    consolidation,
    mtt,
    finalQuoteSellingPriceWeightUnit,
    enquiryType
  } = props;
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [firstRender, setFirstRender] = useState(true);
  const { setValue, control } = useFormContext();

  const weightUnitWatch = useWatch({
    control,
    name: finalQuoteSellingPriceWeightUnit
  });

  const incoTermsWatch = useWatch({
    name: consolidation ? `quote[${props.quoteIndex}].incoTerms` : `quote.${index}.incoTerms`,
    control
  });

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
      return;
    }
    if (item?.isExpanded) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [item?.isExpanded]);

  const productName = item?.productName?.product_name ?? `Product ${index + 1}`;
  const finalQuotePrice = item?.finalQuote_selling_price?.value;
  const finalQuotePriceWeightUnit = item?.finalQuote_selling_price?.weightUnit;
  const finalExWorksPriceCurrency = item?.final_exWorks_price?.currency;
  const finalExWorksPrice = item?.final_exWorks_price?.value;
  const finalExWorksWeightUnit = item?.final_exWorks_price?.weightUnit;
  const finalFobPriceCurrency = item?.fob_price?.currency;
  const finalFobPrice = item?.fob_price?.value;
  const finalFobWeightUnit = item?.fob_price?.weightUnit;
  const finalCfrPriceCurrency = item?.cfr_price?.currency;
  const finalCfrPrice = item?.cfr_price?.value;
  const finalCfrWeightUnit = item?.cfr_price?.weightUnit;
  const finalCifPriceCurrency = item?.cif_price?.currency;
  const finalCifPrice = item?.cif_price?.value;
  const finalCifWeightUnit = item?.cif_price?.weightUnit;
  const finalDdpPrice = item?.ddp_price?.value;
  const finalDdpPriceCurrency = item?.ddp_price?.currency;
  const finalDdpWeightUnit = item?.ddp_price?.weightUnit;
  const cmValues = item?.cmValues;

  const cmChipVariant = {
    positive: 'filled-green',
    negative: 'filled-red',
    zero: 'filled-blue'
  } as const;

  const cmChips = cmValues && (
    <div className={css.cmChips}>
      <Chip
        variant={cmChipVariant[getCMTone(cmValues.cm1)]}
        className={css.cmChip}
        label={`CM1: ${formatCMValue(cmValues.cm1, cmValues.cm1Percent, currency?.label)}`}
      />
      <Chip
        variant={cmChipVariant[getCMTone(cmValues.cm2)]}
        className={css.cmChip}
        label={`CM2: ${formatCMValue(cmValues.cm2, cmValues.cm2Percent, currency?.label)}`}
      />
    </div>
  );

  const handleWeightUnitChange = (value: any) => {
    setValue(finalQuoteSellingPriceWeightUnit, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false
    });
  };

  return (
    <div ref={cardRef} className={css.cardWrapper}>
      <div className={css.cardHeader}>{productName}</div>
      <>
        <div className={css.cardQuotePrice}>
          <div className={css.finalQuotePrice}>Final Selling Price: </div>
          <div className={css.finalQuotePriceValue}>
            {currency ? getCommaSeperatedCurrencyAmount(currency?.label, finalQuotePrice ?? 0) : 0}
            {finalQuotePriceWeightUnit?.label && (
              <div className={css.perAndSelect}>
                <Typography variant="label">(Per</Typography>
                <SelectLabel
                  options={weightOptions}
                  placeholder="Currency"
                  value={weightUnitWatch}
                  onChange={handleWeightUnitChange}
                  rootClassName={css.selectLabel}
                  dropdownDirection={'bottom'}
                />
                <Typography variant="label">)</Typography>
              </div>
            )}
          </div>
        </div>
        {enquiryType === ENQUIRY_TYPE.INTERNATIONAL && (
          <div className={css.cardContent}>
            {!mtt && (
              <div className={css.cardContentItem}>
                <div className={css.cardContentItemHeader}>EX Works</div>
                <div className={css.cardContentItemColon}>:</div>
                <div className={css.cardContentItemValue}>
                  <Typography variant="h5">
                    {finalExWorksPriceCurrency
                      ? getCommaSeperatedCurrencyAmount(
                          finalExWorksPriceCurrency?.label,
                          finalExWorksPrice
                        )
                      : 0}
                    <span>
                      {finalExWorksWeightUnit && (
                        <Typography variant="label" className={css.perWeight}>
                          {' '}
                          Per {finalExWorksWeightUnit?.label}
                        </Typography>
                      )}
                    </span>
                  </Typography>
                </div>
              </div>
            )}
            {incoTermsWatch?.value !== 'EXW' && (
              <div className={css.cardContentItem}>
                <div className={css.cardContentItemHeader}>FOB</div>
                <div className={css.cardContentItemColon}>:</div>
                <div className={css.cardContentItemValue}>
                  <Typography variant="h5">
                    {finalFobPriceCurrency
                      ? getCommaSeperatedCurrencyAmount(finalFobPriceCurrency?.label, finalFobPrice)
                      : 0}
                    <span>
                      {finalFobWeightUnit && (
                        <Typography variant="label" className={css.perWeight}>
                          {' '}
                          Per {finalFobWeightUnit?.label}
                        </Typography>
                      )}
                    </span>
                  </Typography>
                </div>
              </div>
            )}
            {incoTermsWatch?.value !== 'EXW' && incoTermsWatch?.value !== 'FOB' && (
              <div className={css.cardContentItem}>
                <div className={css.cardContentItemHeader}>CFR</div>
                <div className={css.cardContentItemColon}>:</div>
                <div className={css.cardContentItemValue}>
                  <Typography variant="h5">
                    {finalCfrPriceCurrency
                      ? getCommaSeperatedCurrencyAmount(finalCfrPriceCurrency?.label, finalCfrPrice)
                      : 0}
                    <span>
                      {finalCfrWeightUnit && (
                        <Typography variant="label" className={css.perWeight}>
                          {' '}
                          Per {finalCfrWeightUnit?.label}
                        </Typography>
                      )}
                    </span>
                  </Typography>
                </div>
              </div>
            )}
            {incoTermsWatch?.value !== 'EXW' &&
              incoTermsWatch?.value !== 'FOB' &&
              incoTermsWatch?.value !== 'CFR' && (
                <div className={css.cardContentItem}>
                  <div className={css.cardContentItemHeader}>CIF</div>
                  <div className={css.cardContentItemColon}>:</div>
                  <div className={css.cardContentItemValue}>
                    <Typography variant="h5">
                      {finalCfrPriceCurrency
                        ? getCommaSeperatedCurrencyAmount(
                            finalCifPriceCurrency?.label,
                            finalCifPrice
                          )
                        : 0}
                      <span>
                        {finalCifWeightUnit && (
                          <Typography variant="label" className={css.perWeight}>
                            {' '}
                            Per {finalCifWeightUnit?.label}
                          </Typography>
                        )}
                      </span>
                    </Typography>
                  </div>
                </div>
              )}
            {incoTermsWatch?.value == 'DDP' && (
              <div className={css.cardContentItem}>
                <div className={css.cardContentItemHeader}>DDP</div>
                <div className={css.cardContentItemColon}>:</div>
                <div className={css.cardContentItemValue}>
                  <Typography variant="h5">
                    {finalDdpPriceCurrency
                      ? getCommaSeperatedCurrencyAmount(finalDdpPriceCurrency?.label, finalDdpPrice)
                      : 0}
                    <span>
                      {finalDdpWeightUnit && (
                        <Typography variant="label" className={css.perWeight}>
                          {' '}
                          Per {finalDdpWeightUnit?.label}
                        </Typography>
                      )}
                    </span>
                  </Typography>
                </div>
              </div>
            )}
            {cmChips}
          </div>
        )}
        {enquiryType !== ENQUIRY_TYPE.INTERNATIONAL && cmChips}
      </>
    </div>
  );
};

export { CostCalculatorCard };
